import { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { EmptyMessage } from "src/components";
import { PdTableCellText } from "src/components/layout/table";
import { useFocusableQuery } from "src/hooks/api";
import { tColumn } from "src/hooks/table";
import api from "src/util/apiNew";
import { formatMoney, formatPercentage } from "src/util/gFormatting";
import { tPromotionReport } from "tmt_api/src/types/dtos";

import { PdSpinner, ReportTable } from "./components";
import { formatDate, getParams } from "./utils";

const getPromotionsReport = (start: Date, end: Date) =>
  api.get<tPromotionReport[]>("reports/promotions", getParams(start, end)).then((res) => res.data);

const formatDiscount = (row: tPromotionReport) => {
  if (row.discountFee) return formatPercentage(row.discountFee);
  if (row.discountAmount) return formatMoney(row.discountAmount);
  return "-";
};

const columns: tColumn<tPromotionReport>[] = [
  {
    id: "brandName",
    header: "Brand Name",
    accessor: (originalRow) => originalRow.brandName || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "brandEmail",
    header: "Brand Email",
    accessor: (originalRow) => originalRow.brandEmail || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 240 },
  },
  {
    id: "isActivated",
    header: "Active",
    accessor: (originalRow) => (originalRow.isActivated ? "Yes" : "No"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "isPrivate",
    header: "Public/Private",
    accessor: (originalRow) => (originalRow.isPrivate ? "Private" : "Public"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 140 },
  },
  {
    id: "startDateInMilliseconds",
    header: "Date Range",
    accessor: (originalRow) =>
      `${formatDate(originalRow.startDateInMilliseconds / 1000)} - ${formatDate(
        originalRow.endDateInMilliseconds / 1000,
      )}`,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "code",
    header: "Promotion Code",
    accessor: (originalRow) => originalRow.code,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "discountFee", // TODO: how to order by discountFee or discountAmount?
    header: "Promotion Value",
    accessor: (originalRow) => formatDiscount(originalRow),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "activationAmount",
    header: "Minimum Order",
    accessor: (originalRow) => formatMoney(originalRow.activationAmount),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "timesUsed",
    header: "Times Used",
    accessor: (originalRow) => originalRow.timesUsed,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "totalSalesAmount",
    header: "Sales Total",
    accessor: (originalRow) => (originalRow.totalSalesAmount ? formatMoney(originalRow.totalSalesAmount) : "-"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
];

const PromotionsTable: FC<{ period: Date[] }> = ({ period }) => {
  const config = useSelector((state) => state.TableConfigReducer.promotions);
  const { isLoading, data } = useFocusableQuery<tPromotionReport[]>(
    useCallback(() => getPromotionsReport(period[0], period[1]), [period]),
    { enabled: !!(period[0] && period[1]) },
  );

  if (isLoading) {
    return <PdSpinner />;
  }

  if (!data || data?.length === 0) {
    return <EmptyMessage message="There is no data to display" />;
  }

  return (
    <ReportTable
      data={data}
      columns={columns}
      keyExtractor={(item) => item.id}
      sortedBy="brandName"
      columnsConfig={config}
      tableConfigReduxKey="promotions"
    />
  );
};

export default PromotionsTable;
